import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useStores } from '../../hooks/useStore';
import styles from './Reviews.module.css';
import { ReviewListParams } from '../../api/reviews/dto/ReviewListParams.dto';
import { Review } from '../../models/reviews';
import { CreateReviewRequest as APICreateRequest } from '../../api/reviews/dto/CreateReviewRequest.dto';
import { UpdateReviewRequest as APIUpdateRequest } from '../../api/reviews/dto/UpdateReviewRequest.dto';
import { ReviewModal } from './components/ReviewModal';
import { DeleteReviewModal } from './components/DeleteReviewModal';
import { ReviewsTable } from './components/ReviewsTable';
import { ReviewsFilters } from './components/ReviewsFilters';
import { Button } from '../../components/UI/Button/Button';
import { Pagination } from './components/Pagination';

interface ReviewsPageState {
  reviews: Review[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
  loading: boolean;
  filters: ReviewListParams;
}

const ReviewsPage: React.FC = observer(() => {
  const { userStore } = useStores();

  const [state, setState] = useState<ReviewsPageState>({
    reviews: [],
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    loading: true, // Начинаем с состояния загрузки
    filters: {
      page: 1,
      limit: 20,
      sort_by: 'created_at',
      sort_order: 'desc',
    },
  });

  const [modals, setModals] = useState({
    create: false,
    edit: false,
    delete: false,
    selectedReview: null as Review | null,
  });

  // Загрузка отзывов
  const loadReviews = async (params?: Partial<ReviewListParams>) => {
    if (!userStore?.mainStore?.apiClient) {
      return;
    }

    setState((prev) => ({ ...prev, loading: true }));

    try {
      const finalParams = { ...state.filters, ...params };

      // Используем реальный API для получения отзывов
      const response = await userStore.mainStore.apiClient.reviews.getList(
        finalParams,
      );

      if (response.success && response.body) {
        const { reviews, pagination } = response.body;

        setState((prev) => ({
          ...prev,
          reviews: reviews || [],
          totalItems: pagination?.total || 0,
          totalPages: pagination?.total_pages || 1,
          currentPage: pagination?.page || 1,
          filters: finalParams,
          loading: false,
        }));
      } else {
        // Если API не вернул данные, показываем пустой список
        setState((prev) => ({
          ...prev,
          reviews: [],
          totalItems: 0,
          totalPages: 0,
          currentPage: 1,
          loading: false,
        }));
      }
    } catch (error) {
      // Ошибка загрузки отзывов
      // В случае ошибки показываем пустой список
      setState((prev) => ({
        ...prev,
        reviews: [],
        totalItems: 0,
        totalPages: 0,
        currentPage: 1,
        loading: false,
      }));
    }
  };

  // Универсальная функция для создания и обновления отзыва
  const handleSubmitReview = async (
    reviewData: APICreateRequest,
    reviewId?: string,
  ) => {
    if (!userStore?.mainStore?.apiClient) {
      return false;
    }

    try {
      let response;
      if (reviewId) {
        // Обновление существующего отзыва
        response = await userStore.mainStore.apiClient.reviews.update(
          reviewId,
          reviewData as APIUpdateRequest,
        );
      } else {
        // Создание нового отзыва
        response = await userStore.mainStore.apiClient.reviews.create(
          reviewData,
        );
      }

      if (response) {
        await loadReviews(); // Перезагружаем список
        setModals((prev) => ({
          ...prev,
          create: false,
          edit: false,
          selectedReview: null,
        }));
        return true;
      }
      return false;
    } catch (error) {
      // Ошибка при сохранении отзыва
      return false;
    }
  };

  // Удаление отзыва
  const handleDeleteReview = async (reviewId: string) => {
    if (!userStore?.mainStore?.apiClient) {
      return false;
    }

    try {
      // Используем переданный ID для удаления отзыва
      const response = await userStore.mainStore.apiClient.reviews.delete(
        reviewId,
      );

      if (response) {
        await loadReviews(); // Перезагружаем список
        setModals((prev) => ({ ...prev, delete: false, selectedReview: null }));
        return true;
      }
      return false;
    } catch (error) {
      // Ошибка удаления отзыва
      return false;
    }
  };

  // Применение фильтров
  const handleFiltersChange = (newFilters: Partial<ReviewListParams>) => {
    const updatedFilters = { ...state.filters, ...newFilters, page: 1 };
    loadReviews(updatedFilters);
  };

  // Изменение страницы
  const handlePageChange = (page: number) => {
    loadReviews({ ...state.filters, page });
  };

  // Открытие модала редактирования
  const handleEditClick = (review: Review) => {
    setModals({
      ...modals,
      edit: true,
      selectedReview: review,
    });
  };

  // Открытие модала удаления
  const handleDeleteClick = (review: Review) => {
    setModals({
      ...modals,
      delete: true,
      selectedReview: review,
    });
  };

  // Изменение статуса отзыва
  const handleToggleStatus = async (
    reviewId: string,
    currentStatus: Review['status'],
  ) => {
    if (!userStore?.mainStore?.apiClient) {
      return;
    }

    try {
      // Определяем новый статус
      const newStatus = currentStatus === 'published' ? 'draft' : 'published';

      // Создаем минимальный объект обновления
      const updateData: APIUpdateRequest = {
        status: newStatus,
        is_featured: false,
        translations: [
          {
            locale: 'ru',
            slug: '',
            title: '',
          },
        ],
      };

      // Обновляем статус отзыва
      await userStore.mainStore.apiClient.reviews.update(reviewId, updateData);

      // Перезагружаем список
      await loadReviews();
    } catch (error) {
      // Ошибка изменения статуса отзыва
    }
  };

  // Закрытие модалов
  const closeModals = () => {
    setModals({
      create: false,
      edit: false,
      delete: false,
      selectedReview: null,
    });
  };

  // Загрузка при монтировании
  useEffect(() => {
    loadReviews();
  }, [userStore?.mainStore?.apiClient]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Управление отзывами</h1>
        <Button
          appearance="auth"
          onClick={() => setModals((prev) => ({ ...prev, create: true }))}
        >
          + Добавить отзыв
        </Button>
      </div>

      <div className={styles.content}>
        {/* Фильтры */}
        <ReviewsFilters
          filters={state.filters}
          onFiltersChange={handleFiltersChange}
          loading={state.loading}
        />

        {/* Статистика */}
        <div className={styles.stats}>
          <p>
            Всего отзывов: <strong>{state.totalItems}</strong>
          </p>
          <p>
            Страница:{' '}
            <strong>
              {state.currentPage} из {state.totalPages}
            </strong>
          </p>
        </div>

        {/* Таблица отзывов */}
        <ReviewsTable
          reviews={state.reviews}
          loading={state.loading}
          onEdit={handleEditClick}
          onDelete={(reviewId) => {
            const review = state.reviews.find((r) => r.id === reviewId);
            if (review) {
              handleDeleteClick(review);
            }
          }}
          onToggleStatus={handleToggleStatus}
        />

        {/* Пагинация */}
        {state.totalPages > 1 && (
          <Pagination
            currentPage={state.currentPage}
            totalPages={state.totalPages}
            totalItems={state.totalItems}
            itemsPerPage={state.filters.limit || 20}
            onPageChange={handlePageChange}
            loading={state.loading}
          />
        )}
      </div>

      {/* Модалы */}
      {/* Создание нового отзыва */}
      {modals.create && (
        <ReviewModal
          isOpen={modals.create}
          onClose={closeModals}
          onSubmit={handleSubmitReview}
        />
      )}

      {/* Редактирование существующего отзыва */}
      {modals.edit && modals.selectedReview && (
        <ReviewModal
          isOpen={modals.edit}
          onClose={closeModals}
          onSubmit={handleSubmitReview}
          reviewId={modals.selectedReview.id}
          existingReview={modals.selectedReview}
        />
      )}

      {/* Удаление отзыва */}
      {modals.delete && modals.selectedReview && (
        <DeleteReviewModal
          isOpen={modals.delete}
          onClose={closeModals}
          onConfirm={handleDeleteReview}
          review={modals.selectedReview}
        />
      )}
    </div>
  );
});

export default ReviewsPage;
